import type { IRequestOpts, IResultstash, JsonQuery } from "@mne-core/api";
import { QueryController, createPostRequestOpts, createUrl, fetchJsonQuery } from "@mne-core/api";

import type { IAuthenticationData } from "../@types";

/**
 * Represents a request for password authentication.
 *
 * Requires password authentication to be enabled on the server. Use the `/methods` endpoint to check which
 * authentication methods are available on the server. The client can optionally request specific
 * authorizations as part of the authentication transaction, though these authorizations are not
 * guaranteed to be granted. The `setPassword` authorization, is only returned if
 * the current user has the `FORCE_PW_CHANGE` flag set.
 *
 * @interface IPasswordAuthenticationRequest
 * @property {string} userName - The username to authenticate.
 * @property {string} password - The LDAP password for the user.
 * @property {string} domain - The user's domain.
 * @property {("enrollPublicKey" | "setPassword")[]} [requestedAuthorizations] - Allows the client to request authorizations as part of the authentication transaction. The client is not guaranteed to get the requested authorizations. The `setPassword` authorization is only returned if the current user has the `FORCE_PW_CHANGE` flag set. Valid values are `enrollPublicKey` for requesting public key enrollment and `setPassword` for requesting password setting.
 */

export interface IPasswordAuthenticationRequest {
  userName: string;
  password: string;
  domain: string;
  requestedAuthorizations?: ["enrollPublicKey" | "setPassword"];
}

export const createRequest = (criteria: IPasswordAuthenticationRequest, opts: IRequestOpts): Request => {
  const { apiUrl } = opts;
  const url = createUrl(apiUrl, ["authentication", "v1", "password", "authentication"]).toString();
  return new Request(url, createPostRequestOpts(JSON.stringify(criteria), opts));
};

async function authenticatePassword(
  criteria: IPasswordAuthenticationRequest,
  opts: IRequestOpts,
): Promise<JsonQuery<IResultstash<IAuthenticationData>>> {
  return await fetchJsonQuery<IResultstash<IAuthenticationData>>(createRequest(criteria, opts), {
    ctrl: opts.ctrl || new QueryController(),
  });
}

export default authenticatePassword;
